import { Header } from "@/components/header";
import { ThaloDataTable } from "@/components/thalo-data-table";
import { Card } from "@/components/ui/card";
import React, { useState } from "react";
import { PieChart } from "./pie_chart";
import { AssetHealthDataColumns } from "./data_columns";
import QuickTooltip from "@/components/quick-tooltip";
import { Checkbox } from "@/components/ui/checkbox"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import { useSuspenseQuery } from "@tanstack/react-query";
import { SortingState } from "@tanstack/react-table";
import { SensorKitModel } from "@/sensorKits/sensorKit";
import { get, PaginatedResponse } from "@/api/api";

interface AlertStatus {
  id: number;
  sensor_kit: string;
  version: string;
  alert_count: number;
  gary_status: number;
  alert_count_L0: number;
  alert_count_L1: number;
  alert_count_L2: number;
  alert_count_L3: number;
  current_installation_location: string;
  current_installation_name: string;
  grafana_url: string;
}

interface AlertFilters {
  active: boolean;
  snooze_triggered: boolean;
  acknowledged: boolean;
  sensorkit__config__model: SensorKitModel | "";
}

const useAlerts = ({
  page = 1,
  pageSize = 1000,
  sorting,
  filters,
}: {
  page?: number;
  pageSize?: number;
  sorting?: SortingState;
  filters: AlertFilters;
}) => {
  let url = `/alerts/install-statuses/?page=` + page;
  url += "&page_size=" + pageSize;
  if (sorting?.length) {
    url += "&ordering=" + (sorting[0]?.desc ? "-" : "") + sorting[0]?.id;
  }
  if (filters.active) {
    url += "&active=true";
  } else {
    url += "&active=false"; // Explicitly handle the negative case
  }

  if (filters.snooze_triggered) {
    url += "&snoozed=true";
  } else {
    url += "&snoozed=false"; // Explicitly handle the negative case
  }

  if (filters.acknowledged) {
    url += "&acknowledged=true";
  } else {
    url += "&acknowledged=false"; // Explicitly handle the negative case
  }

  if (filters.sensorkit__config__model) {
    url += `&sensorkit__config__model=${filters.sensorkit__config__model}`;
  }
  return useSuspenseQuery({
    queryKey: ["alert-statuses", page, pageSize, sorting, filters],
    queryFn: async () => {
      return await get<PaginatedResponse<AlertStatus>>(url);
    },
  });
};

const AssetHealth = () => {
  const [sorting, setSorting] = useState([{ id: "serial_number", desc: false }]);
  const [activeFilter, setActiveFilter] = useState(true);
  const [snoozedFilter, setSnoozedFilter] = useState(false);
  const [acknowledgedFilter, setAcknowledgedFilter] = useState(false);
  const [modelFilter, setModelFilter] = useState<SensorKitModel | "">("");

  const { data: alerts } = useAlerts({
    page: 1,
    pageSize: 1000,
    sorting,
    filters: {
      active: activeFilter,
      snooze_triggered: snoozedFilter,
      acknowledged: acknowledgedFilter,
      sensorkit__config__model: modelFilter
    }
  });

  const alertData = React.useMemo(() => {
    const data = { noAlerts: 0, L0: 0, L1: 0, L2: 0 };
    alerts.results.forEach((alert) => {
      if (alert.alert_count === 0) {
        data.noAlerts += 1;
      } else if (alert.alert_count_L0 > 0) {
        data.L0 += 1;
      } else if (alert.alert_count_L1 > 0) {
        data.L1 += 1;
      } else if (alert.alert_count_L2 > 0) {
        data.L2 += 1;
      }
    });
    return data;
  }, [alerts]);

  return (
    <>
      <div className="grid grid-cols-12 gap-x-4 gap-y-2 grid-ster">
        <div className="col-span-full">
          <Header
            name={`Sensor Kit Health`}
            showDatePicker={false}
            showChartOptions={false}
            className="font-normal text-4xl"
          />
        </div>
        <div className="col-span-7">
          <Card className="p-4">
            <QuickTooltip text="Counts number of sensor kits with alerts in at least the given category.">
              <h1><b>Sensor Kit Count by Alert Type</b></h1>
            </QuickTooltip>
            <div className="col-span-12" style={{ height: '250px' }}>
              <PieChart data={alertData}/>
            </div>
          </Card>
        </div>
        <div className="col-span-full">
          <Card className="p-4">
            <div className="flex justify-end">
              <div className="border rounded-lg p-4">
                <h2 className="font-semibold mb-2">Filters</h2>
                <div className="flex space-x-4 items-center">
                  <QuickTooltip text="Disable to also see alerts that have been deactivated (could still be firing).">
                    <Checkbox
                      label="Include Inactive Alerts"
                      checked={activeFilter}
                      onCheckedChange={setActiveFilter}
                    />
                  </QuickTooltip>
                  <QuickTooltip text="Useful for also seeing alerts that have been snoozed (could still be firing).">
                    <Checkbox
                      label="Include Snoozed Alerts"
                      checked={snoozedFilter}
                      onCheckedChange={setSnoozedFilter}
                    />
                  </QuickTooltip>
                  <QuickTooltip
                    text="Useful for also seeing alerts that have been acknowledged (could still be firing).">
                    <Checkbox
                      label="Include Acknowledged Alerts"
                      checked={acknowledgedFilter}
                      onCheckedChange={setAcknowledgedFilter}
                    />
                  </QuickTooltip>
                  <div className="ml-4">
                    <QuickTooltip text="Filter by sensor kit model type">
                      <Select value={modelFilter} onValueChange={(value: string) => setModelFilter(value as SensorKitModel | "")}>
                        <SelectTrigger className="w-[180px]">
                          <SelectValue placeholder="Filter by Model"/>
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="">All Models</SelectItem>
                          <SelectItem value="combustion">Combustion</SelectItem>
                          <SelectItem value="point_capture">Point Capture</SelectItem>
                          <SelectItem value="direct_air_capture">Direct Air Capture</SelectItem>
                          <SelectItem value="refrigerant">Refrigerant</SelectItem>
                        </SelectContent>
                      </Select>
                    </QuickTooltip>
                  </div>
                </div>
              </div>
            </div>
            <ThaloDataTable
              variant="thalo"
              data={alerts.results || []}
              columns={AssetHealthDataColumns()}
              isLoading={false}
              pageCount={Math.ceil(
                alerts.count / alerts.page_size
              )}
              sorting={sorting}
              onSortingChange={setSorting}
              itemsPerPage={1000}
            />
          </Card>
        </div>
      </div>
    </>
  );
};

export default AssetHealth;